import { createElement, MouseEvent } from 'react';
import DOMPurify from 'dompurify';
import { notifications, notificationsStore } from '@mantine/notifications';
import type { INotification, INotificationAction } from '../molecules/Notifications/INotification';
import { errorTraceId, TRACE_ID_LABEL } from '@srm/core/errors';
import TraceJournalLink from '../atoms/TraceJournalLink';

const variantColorMap: Record<NonNullable<INotification['variant']>, string> = {
    default: 'blue',
    blue: 'blue',
    green: 'green',
    yellow: 'yellow',
    red: 'red',
    white: 'gray',
};

/** Удалить уведомление */
export const removeNotification = (id?: INotification['id']) => {
    if (id !== undefined && id !== '') {
        notifications.hide(String(id));
        return;
    }

    const state = notificationsStore.getState();
    const firstNotificationId = state.notifications[0]?.id ?? state.queue[0]?.id;

    if (firstNotificationId) {
        notifications.hide(firstNotificationId);
    }
};

/**
 * Код обращения уведомления. Кроме явного поля берётся из самого сообщения: в вызовах
 * вида `sendNotification({ message: reason })` reason — это то, чем отклонил интерцептор,
 * с прикреплённым кодом (`withTraceId` из `@srm/core/errors`).
 */
const traceIdOf = (notification: INotification): string | undefined =>
    errorTraceId(notification) ?? errorTraceId(notification.message);

/**
 * Код обращения под текстом ошибки: пользователь называет его в поддержку, поддержка ищет
 * по нему запись в журнале. Кнопка рядом — 32 символа по телефону не диктуют.
 */
const traceIdNode = (traceId: string) => {
    // Без Clipboard API (небезопасный контекст) кнопка бессмысленна — код остаётся
    // доступен выделением.
    const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;

    return createElement(
        'span',
        { className: 'rf-notification-trace', key: 'trace' },
        `${TRACE_ID_LABEL}: `,
        createElement('code', { className: 'rf-notification-trace__value' }, traceId),
        clipboard &&
            createElement(
                'button',
                {
                    type: 'button',
                    className: 'rf-notification-trace__copy',
                    title: 'Скопировать код обращения',
                    onClick: (event: MouseEvent<HTMLButtonElement>) => {
                        // Отказ реален: страница без фокуса, запрет Permissions-Policy.
                        // Молчать нельзя — человек уйдёт в поддержку без кода.
                        const button = event.currentTarget;
                        clipboard.writeText(traceId).then(
                            () => {
                                button.textContent = 'Скопировано';
                            },
                            () => {
                                button.textContent = 'Не удалось скопировать';
                            },
                        );
                    },
                },
                'Копировать',
            ),
        createElement(TraceJournalLink, { traceId }),
    );
};

/** Действие уведомления: нажатие закрывает его и выполняет действие. */
const actionNode = (id: string, action: INotificationAction) =>
    createElement(
        'button',
        {
            type: 'button',
            key: 'action',
            className: 'rf-notification-action',
            onClick: () => {
                notifications.hide(id);
                action.onClick();
            },
        },
        action.label,
    );

/** Добавить уведомление */
export const sendNotification = (notification: INotification, delay = 8000) => {
    if (!notification.title && !notification.message) {
        return;
    }

    const id = notification.id?.toString() || Date.now().toString();
    const traceId = traceIdOf(notification);
    const textNode = notification.message
        ? createElement('span', {
              key: 'text',
              dangerouslySetInnerHTML: { __html: DOMPurify.sanitize(String(notification.message)) },
          })
        : null;
    const extras = [traceId && traceIdNode(traceId), notification.action && actionNode(id, notification.action)].filter(
        Boolean,
    );
    const messageNode = extras.length
        ? createElement('span', { className: 'rf-notification-body' }, textNode, ...extras)
        : textNode;

    notifications.show({
        id,
        title: notification.title,
        message: messageNode,
        color: notification.variant ? variantColorMap[notification.variant] : undefined,
        autoClose: delay,
        withCloseButton: true,
    });
};
